/**
 * Copyright ©2024 Drivepoint
 */

import {closeSnackbar, enqueueSnackbar, SnackbarKey} from "notistack";
import {Button} from "@mui/material";
import {DPError} from "../utilities/DPError";
import ErrorHandling from "../utilities/ErrorHandling";

type ToastErrorInfo = {
  message: string;
  reload?: boolean;
  contactSupport?: boolean;
};

export default class Toast {

  static error(error: Error): void {
    if (!error) { return; }

    if (DPError.isDPError(error)) {
      Toast._prepareDPError(error);
      return;
    }

    const errorInfo = Toast._getErrorInfo(error);
    const action = (snackbarId: SnackbarKey | undefined) => (
      <>
        {errorInfo.reload && <Button color="inherit" onClick={() => { window.location.reload(); }}>Refresh</Button>}
        {<Button color="inherit" onClick={Toast._contactSupport}>Contact Support</Button>}
        <Button color="inherit" onClick={() => { closeSnackbar(snackbarId); }}>Dismiss</Button>
      </>
    );
    enqueueSnackbar(errorInfo.message, {persist: true, action, variant: "error", anchorOrigin: {horizontal: "center", vertical: "top"}});
  }

  static success(message: string): void {
    if (!message) { return; }
    const action = (snackbarId: SnackbarKey | undefined) => (
      <Button color="inherit" onClick={() => { closeSnackbar(snackbarId); }}>Dismiss</Button>
    );
    enqueueSnackbar(message, {action, variant: "success", anchorOrigin: {horizontal: "center", vertical: "top"}});
  }

  static progress(message: string): SnackbarKey | undefined {
    if (!message) { return; }
    return enqueueSnackbar(
      message, 
      {
        persist: true, 
        anchorOrigin: {horizontal: "center", vertical: "top"},
        style: {maxWidth: "400px", margin: "0 auto", marginTop: "40px"}
      });
  }

  private static _prepareDPError(error: DPError): void {
    const formattedError = ErrorHandling.handle(error);
    const action = (snackbarId: SnackbarKey | undefined) => <>
      {formattedError.action && <Button color="inherit" onClick={() => { formattedError.action.onClick(); }}>{formattedError.action.label}</Button>}
      <Button color="inherit" onClick={() => { closeSnackbar(snackbarId); }}>Dismiss</Button>
    </>;
    enqueueSnackbar(formattedError.message, {persist: true, action, variant: "error", anchorOrigin: {horizontal: "center", vertical: "top"}});
  }

  private static _contactSupport(event: any): void {
    event?.preventDefault();
    event?.stopPropagation();
    window.open("https://docs.drivepoint.io/help/help-1/", "_blank");
  }

  private static _getErrorInfo(error: any): ToastErrorInfo {
    const info: ToastErrorInfo = {message: error.message};
    if (error instanceof TypeError) {
      if (error.message === "Failed to fetch") {
        info.message = "There was a problem connecting to our service, please wait and try again.";
      } else {
        info.message = "An unexpected error occurred, please refresh the page and try again.";
      }
      info.reload = true;
    }
    switch (error.code) {
      case 409:
        info.message = "This item already exists. Please choose a new one";
        break;
      case 404:
        info.message = "The item was not found.";
        break;
      case 423:
        info.message = "The item is currently locked, please try again.";
        break;
    }
    return info;
  }

}
